require('./RADistributionWidget.scss');

// @vendors
const React = require('react');
const PropTypes = require('prop-types');
const classNames = require('classnames');

// @commons
const { OKButton } = require('components/commons/OKButton/OKButton');
const RAPieChart = require('../commons/RAPieChart/RAPieChart');
const RAOneLineSymbolAmount = require('../commons/RAOneLineSymbolAmount/RAOneLineSymbolAmount');
const OKTooltip = require('components/commons/OKTooltip/OKTooltip');
const { OKLink } = require('commonsComponents/OKLink');

// @utilities
const { FormattedText } = require('core/i18n').i18n;
const {
    getAssetIdsToIncreaseByOne,
    formatPercentageWithLessOne,
} = require('../../utilities/RACurrencyHelper');
const RAAssetsHelper = require('../../utilities/RAAssetsHelper');
const { formatAmount } = require('utilities/currencyHelper');
const {
    formatWithoutRoundedPercentage,
} = require('../../utilities/RACurrencyHelper');
const {
    recordLink,
    PROC_RA_GLOBAL_POSITION_INTERACTION,
} = require('utilities/tagging');

// @constants
const { PERCENTAGE_SYMBOL, EURO_SYMBOL } = require('constants/index');

// @components
const RAServiceCostModal = require('../RAServiceCostModal/RAServiceCostModal');
const RAInformativeModal = require('../RAInformativeModal/RAInformativeModal');

const {
    ACTIVE_CLASS_MONEY_MARKET_ID,
    ACTIVE_CLASS_FIXED_INCOME_ID,
    ACTIVE_CLASS_EQUITY_ID,
    ACTIVE_CLASS_REAL_ASSETS_ID,
    PROC_RA_HIRE,
    PROC_RA_GLOBAL_POSITION_TOTAL_PATRIMONY,
} = require('../../raConstants/index');

class RADistributionWidget extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isModalOpen: false,
            isMoneyMarketModalOpen: false,
        };
        this.handleHireClick = this.handleHireClick.bind(this);
        this.handleOnLinkClick = this.handleOnLinkClick.bind(this);
        this.handleCloseModal = this.handleCloseModal.bind(this);
        this.buildModal = this.buildModal.bind(this);
        this.onMoneyMarketLinkClick = this.onMoneyMarketLinkClick.bind(this);
    }

    handleHireClick() {
        this.props.validateResidential(this.props.link);
        recordLink(
            PROC_RA_GLOBAL_POSITION_INTERACTION,
            PROC_RA_HIRE,
            PROC_RA_GLOBAL_POSITION_TOTAL_PATRIMONY,
        );
    }

    handleCloseModal() {
        this.setState({
            isModalOpen: false,
            isMoneyMarketModalOpen: false,
        });
    }

    handleOnLinkClick() {
        const { calculateLinkClick } = this.props;
        if (!!calculateLinkClick) {
            calculateLinkClick();
        }
        this.setState({
            isModalOpen: true,
        });
    }

    onMoneyMarketLinkClick() {
        this.props.handleMoneyMarketLinkClick();
        this.setState({
            isMoneyMarketModalOpen: true,
        });
    }

    getPieChartData(immAssetAllocations, hasAnyData) {
        const assetIdsToIncrease = getAssetIdsToIncreaseByOne(
            immAssetAllocations,
            hasAnyData,
        );

        const data = immAssetAllocations.map(asset => {
            const key = asset.get('assetClassId');
            const needCorrectionWeight = assetIdsToIncrease.includes(key);
            const value = Math.floor(asset.get('weight') * 100) / 100;

            return {
                key: key,
                label: RAAssetsHelper.getAssetClassName(
                    asset.get('assetClassId'),
                ),
                value: needCorrectionWeight ? value + 0.01 : value,
            };
        });

        return data;
    }

    getTextClass(className, inactive) {
        const customClass = classNames(className, {
            [`${className}--inactive-text`]: inactive,
        });

        return customClass;
    }

    getTabItemClass(assetClassId, inactive) {
        const customClass = classNames('ra-distribution-widget__item-point', {
            ['ra-distribution-widget__item-point--money-market']:
                assetClassId === ACTIVE_CLASS_MONEY_MARKET_ID,
            ['ra-distribution-widget__item-point--fixed-income']:
                assetClassId === ACTIVE_CLASS_FIXED_INCOME_ID,
            ['ra-distribution-widget__item-point--equity']:
                assetClassId === ACTIVE_CLASS_EQUITY_ID,
            ['ra-distribution-widget__item-point--real-assets']:
                assetClassId === ACTIVE_CLASS_REAL_ASSETS_ID,
            ['ra-distribution-widget__item-point--inactive']: inactive,
        });

        return customClass;
    }

    buildTabs(immAssetAllocations, hasAnyData, hasRoboHired) {
        const assets = RAAssetsHelper.getEmptyStateAssets();
        let isActiveText = false;
        const assetIdsToIncrease = getAssetIdsToIncreaseByOne(
            immAssetAllocations,
            hasAnyData,
        );

        const tabs = assets.map(asset => {
            let validAssetData;
            let immAssetAllocated;
            if (hasAnyData) {
                immAssetAllocated = immAssetAllocations.find(
                    immAsset =>
                        immAsset.get('assetClassId') === asset.assetClassId,
                );
                validAssetData = immAssetAllocated
                    ? immAssetAllocated.toJSON()
                    : asset;
                isActiveText = true;
            } else {
                validAssetData = asset;
            }

            const inactiveThisText =
                !hasRoboHired || (validAssetData.weight === 0 && !isActiveText);
            const assetClassId = validAssetData.assetClassId;
            const assetClassName = RAAssetsHelper.getAssetClassName(
                assetClassId,
            );
            const needCorrectionWeight = assetIdsToIncrease.includes(
                validAssetData.assetClassId,
            );
            const assetWeight = formatPercentageWithLessOne(
                validAssetData.weight,
                needCorrectionWeight,
            );
            const itemClassName = this.getTabItemClass(
                assetClassId,
                inactiveThisText,
            );
            const itemTitleClassName = this.getTextClass(
                'ra-distribution-widget__item-title',
                inactiveThisText,
            );
            const itemBodyClassName = this.getTextClass(
                'ra-distribution-widget__item-body',
                inactiveThisText,
            );
            const itemSymbolClassName = this.getTextClass(
                'ra-distribution-widget__item-symbol',
                inactiveThisText,
            );

            return (
                <div
                    key={assetClassId}
                    className="ra-distribution-widget__tab-item"
                >
                    <div className={itemClassName} />
                    <div className={itemTitleClassName}>{assetClassName}</div>
                    <div className={itemBodyClassName}>
                        <span>{assetWeight}</span>
                        <span className={itemSymbolClassName}>
                            {PERCENTAGE_SYMBOL}
                        </span>
                    </div>
                </div>
            );
        });

        return tabs;
    }

    buildInfo() {
        return (
            <p className="ra-distribution-widget__info">
                {this.props.distributionInfo}
            </p>
        );
    }

    buildSubtitle() {
        return (
            <p className="ra-distribution-widget__info">
                {this.props.distributionSubTitle}
            </p>
        );
    }

    buildMoneyMarketLink(smallWidget) {
        let moneyMarketLink;
        const moneyMarket = this.props.immRoboAdvisor
            .get('assetAllocation')
            .find(
                immClass =>
                    immClass.get('assetClassId') ===
                    ACTIVE_CLASS_MONEY_MARKET_ID,
            );
        const moneyMarketWeight = moneyMarket && moneyMarket.get('weight');

        if (moneyMarketWeight === 1) {
            const linkClassName = classNames({
                'row no-gutter ra-distribution-widget__row ra-distribution-widget__row--last': !smallWidget,
                'ra-distribution-widget__money-market-link': smallWidget,
            });

            moneyMarketLink = (
                <div className={linkClassName}>
                    <OKLink
                        onClick={this.onMoneyMarketLinkClick} // eslint-disable-line
                        modifier="complementary"
                    >
                        <FormattedText value="raDistributionWidget-raDistributionWidget_moneyMarketLink" />
                    </OKLink>
                </div>
            );
        }

        return moneyMarketLink;
    }

    buildCalculateDataLink() {
        const moneyMarketLink = this.buildMoneyMarketLink(false);

        return (
            <div className="col-md-8">
                {moneyMarketLink}
                <div className="row no-gutter ra-distribution-widget__row ra-distribution-widget__row--last">
                    <OKLink
                        onClick={this.handleOnLinkClick}
                        modifier="complementary"
                    >
                        <FormattedText value="raDistributionWidget-raDistributionWidget_howWeCalculateThisData" />
                    </OKLink>
                </div>
            </div>
        );
    }

    buildButton() {
        return (
            <div className="ra-distribution-widget__button-wrap">
                <OKButton onClick={this.handleHireClick}>
                    <FormattedText value="raDistributionWidget-ctaHire" />
                </OKButton>
            </div>
        );
    }

    buildProfitabilityTooltip() {
        return (
            <OKTooltip
                content="raDistributionWidget-profitabilitiyTooltip"
                offset={[0, -10]}
            />
        );
    }

    buildModal(buildServiceCostModal) {
        return (
            <RAServiceCostModal
                isModalOpen={this.state.isModalOpen}
                onModalClose={this.handleCloseModal}
                titleName={
                    'raDistributionWidget-raDistributionWidget_howWeCalculateThisData'
                }
                extraColumnForProfit={buildServiceCostModal}
            />
        );
    }

    buildMoneyMarketModal() {
        return (
            <RAInformativeModal
                isModalOpen={this.state.isMoneyMarketModalOpen}
                onModalClose={this.handleCloseModal}
                textKey="raInvestmentMoneyMarketModal"
            />
        );
    }

    buildWidgetBody() {
        const {
            hasRoboHired,
            immRoboAdvisor,
            buildServiceCostModal,
            showSimpleReturnPercent, // showSimpleReturnPercent it was wrong mapped now this should be showTimeWeightedReturn
            showProfitabilityTooltip,
        } = this.props;
        const immAssetAllocations = immRoboAdvisor.get('assetAllocation');
        const hasData = immAssetAllocations && !immAssetAllocations.isEmpty();
        const inactiveText = !hasData && !hasRoboHired;
        const headerItemClassName = this.getTextClass(
            'ra-distribution-widget__header-item',
            inactiveText,
        );
        const currencySecondaryClassName = this.getTextClass(
            'ra-distribution-widget__currency-secondary',
            inactiveText,
        );
        const percentageClassName = this.getTextClass(
            'ra-distribution-widget__percentage',
            inactiveText,
        );
        const currencyPrimaryClassName = this.getTextClass(
            'ra-distribution-widget__currency-primary',
            inactiveText,
        );
        const valuation = immRoboAdvisor.getIn(['totals', 'valuation']);
        const netReturn = immRoboAdvisor.getIn(['totals', 'netReturn']);
        const timeWeightedReturn = immRoboAdvisor.getIn([
            'totals',
            'timeWeightedReturn',
        ]);
        const deposits = immRoboAdvisor.getIn(['totals', 'deposits']);
        const withdrawals = immRoboAdvisor.getIn(['totals', 'withdrawals']);
        const profitabilityTooltip =
            hasRoboHired && showProfitabilityTooltip
                ? this.buildProfitabilityTooltip()
                : null;
        const modal = this.state.isModalOpen
            ? this.buildModal(buildServiceCostModal)
            : null;

        const currencyComposedClasses = classNames(
            'ra-distribution-widget__currency-composed',
            {
                'ra-distribution-widget__currency-composed--first': showSimpleReturnPercent,
            },
        );
        const timeWeightedReturnPercentElement = showSimpleReturnPercent && (
            <div className="ra-distribution-widget__currency-composed ra-distribution-widget__currency-composed--last">
                <span className={percentageClassName}>
                    {formatAmount(
                        formatWithoutRoundedPercentage(
                            timeWeightedReturn,
                            false,
                        ),
                    )}
                    {PERCENTAGE_SYMBOL}
                </span>
            </div>
        );

        return (
            <div className="col-md-8">
                <div className="ra-distribution-widget__right-content">
                    <div className="row no-gutter ra-distribution-widget__row ra-distribution-widget__row--first">
                        <div className="col-md-4">
                            {modal}
                            <div className={headerItemClassName}>
                                <FormattedText value="raDistributionWidget-currentValue" />
                            </div>
                            <div className={currencyPrimaryClassName}>
                                <RAOneLineSymbolAmount
                                    amountObject={{
                                        amount: valuation,
                                        currency: EURO_SYMBOL,
                                    }}
                                />
                            </div>
                        </div>
                        <div className="col-md-8">
                            <div className={headerItemClassName}>
                                <FormattedText value="raDistributionWidget-raDistributionWidget_gatherProfitability" />
                                {profitabilityTooltip}
                            </div>
                            <div>
                                <div className={currencyComposedClasses}>
                                    <span className={currencyPrimaryClassName}>
                                        <RAOneLineSymbolAmount
                                            amountObject={{
                                                amount: netReturn,
                                                currency: EURO_SYMBOL,
                                            }}
                                        />
                                    </span>
                                </div>
                                {timeWeightedReturnPercentElement}
                            </div>
                        </div>
                    </div>
                    <div className="row no-gutter ra-distribution-widget__row ra-distribution-widget__row--last">
                        <div className="col-md-4">
                            <div className={headerItemClassName}>
                                <FormattedText value="raDistributionWidget-incomings" />
                            </div>
                            <div className={currencySecondaryClassName}>
                                <RAOneLineSymbolAmount
                                    amountObject={{
                                        amount: deposits,
                                        currency: EURO_SYMBOL,
                                    }}
                                />
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className={headerItemClassName}>
                                <FormattedText value="raDistributionWidget-outgoings" />
                            </div>
                            <div className={currencySecondaryClassName}>
                                <RAOneLineSymbolAmount
                                    amountObject={{
                                        amount: withdrawals,
                                        currency: EURO_SYMBOL,
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const {
            buildWidget,
            distributionInfo,
            distributionTitle,
            hasRoboHired,
            immRoboAdvisor,
            link,
            pieHeight,
            pieWeight,
            pieWidth,
            showCalulationInfoLink,
        } = this.props;

        const immAssetAllocations = immRoboAdvisor.get('assetAllocation');
        const hasData = immAssetAllocations && !immAssetAllocations.isEmpty();
        const assetsTabs = this.buildTabs(
            immAssetAllocations,
            hasData,
            hasRoboHired,
        );
        const assetData = this.getPieChartData(immAssetAllocations, hasData);
        const info =
            !hasData && !hasRoboHired && distributionInfo
                ? this.buildInfo()
                : null;
        const calculateDataLink =
            hasRoboHired && showCalulationInfoLink
                ? this.buildCalculateDataLink()
                : null;
        const button = link && !hasRoboHired ? this.buildButton() : null;
        const widgetBody = buildWidget && this.buildWidgetBody();
        const moneyMarketLink = !buildWidget && this.buildMoneyMarketLink(true);
        const pieChartWrapperClassName = classNames(
            'ra-distribution-widget__pie-chart-wrap',
            {
                'ra-distribution-widget__pie-chart-wrap--small': !buildWidget,
            },
        );
        const pieChartClassName = classNames({
            'col-md-4': buildWidget,
            'col-md-12': !buildWidget,
        });
        const distributionTitleElement = distributionTitle && (
            <h2 className="ra-distribution-widget__title">
                {distributionTitle}
            </h2>
        );
        const moneyMarketModal = this.buildMoneyMarketModal();
        return (
            <div className="ra-distribution-widget">
                <div className="row no-gutter ra-distribution-widget__header">
                    <div className="col-md-12">
                        {distributionTitleElement}
                        {this.buildSubtitle()}
                        {info}
                    </div>
                </div>
                <div className="row no-gutter ra-distribution-widget__body">
                    <div className={pieChartClassName}>
                        <div className={pieChartWrapperClassName}>
                            <RAPieChart
                                data={assetData}
                                height={pieHeight}
                                weight={pieWeight}
                                width={pieWidth}
                            />
                        </div>
                    </div>
                    {widgetBody}
                    {calculateDataLink}
                </div>
                <div className="row no-gutter ra-distribution-widget__footer">
                    <div className="col-md-12">
                        <div className="ra-distribution-widget__tabs clearfix">
                            {assetsTabs}
                            {moneyMarketLink}
                            {moneyMarketModal}
                        </div>
                        {button}
                    </div>
                </div>
            </div>
        );
    }
}

RADistributionWidget.propTypes = {
    hasRoboHired: PropTypes.bool.isRequired,
    immRoboAdvisor: PropTypes.object.isRequired,
    link: PropTypes.string,
    buildWidget: PropTypes.bool,
    enableAssetsTabsSidebarDesign: PropTypes.bool,
    showCalulationInfoLink: PropTypes.bool,
    distributionInfo: PropTypes.string,
    distributionTitle: PropTypes.string,
    distributionSubTitle: PropTypes.string,
    pieHeight: PropTypes.number,
    pieWeight: PropTypes.number,
    pieWidth: PropTypes.number,
    validateResidential: PropTypes.func.isRequired,
    buildServiceCostModal: PropTypes.bool.isRequired,
    showSimpleReturnPercent: PropTypes.bool,
    showProfitabilityTooltip: PropTypes.bool,
    calculateLinkClick: PropTypes.func,
    handleMoneyMarketLinkClick: PropTypes.func,
};

RADistributionWidget.defaultProps = {
    buildWidget: true,
    enableAssetsTabsSidebarDesign: false,
    showCalulationInfoLink: true,
    buildServiceCostModal: false,
    showSimpleReturnPercent: false,
    showProfitabilityTooltip: true,
    calculateLinkClick: () => {},
    handleMoneyMarketLinkClick: () => {},
};
module.exports = RADistributionWidget;
